import * as types from "./action-types";
import api from "../api";
import * as initService from "../bl/initService";
import * as routeService from "../bl/routeService";
import SearchRouteParameters from "../models/SearchRouteParameters";
import {getRouteInfo, getVisualRoute} from "../bl/routeResponseUtils";
import {batch} from "react-redux";
import {routeMetricsCalculator} from "../utils/metricsCalculator";
import {createKeyPointFromParsedPoints} from "../utils";
import {createRouteRequestFromKeyPoints, routeRequestPointIsSet} from "../bl/routeRequestUtils";
import {ROUTE_FOUND_WITHOUT_RESTRICTIONS} from "../constants"
import geoStorages from "../utils/SessionStorageRingBuffer";
import RouteSegment from "../models/RouteSegment";
import RouteDetails from "../models/RouteDetails";
import RouteResponsePoint from "../models/RouteResponsePoint";
import RouteRequest from "../models/RouteRequest";
import NODE_TYPES from "../constants/nodeTypes";
import {excludePassthroughPointFromRoute} from "../bl/routeService";

export const increasePendingRequest = () => ({
  type: types.INC_PENDING_REQUESTS,
});

export const decreasePendingRequest = () => ({
  type: types.DEC_PENDING_REQUESTS,
});

/** @param {AutoCompleteResult[]} suggestions */
export const fetchSuggestionsSucceeded = (suggestions) => ({
  type: types.FETCH_SUGGESTIONS_SUCCEEDED,
  suggestions,
});

export const clearSuggestions = () => ({
  type: types.CLEAR_SUGGESTIONS,
});

export const fetchSuggestions = query => dispatch => {

  api.geo
    .getSuggestions(query)
    .then(suggestions => dispatch(fetchSuggestionsSucceeded(suggestions)));
};

export const toggleLegendPopup = isOpen => ({
  type: types.TOGGLE_LEGEND_POPUP,
  isOpen,
});

export const showResults = show => ({
  type: types.SHOW_RESULTS,
  show,
});

export const savePlatonRate = platonRate => ({
  type: types.SAVE_PLATON_RATE,
  platonRate: platonRate,
});

export const routeNotFoundFlag = () => ({
  type: types.ROUTE_NOT_FOUND,
  routeNotFound: true,
});

export const routeWithoutRestrictionsFlag = (withoutRestrictions) => ({
  type: types.ROUTE_WITHOUT_RESTRICTIONS,
  routeWithoutRestrictions: withoutRestrictions,
});

/** @param {RouteRequest} routeRequest */
export const updateRouteRequest = (routeRequest) => {
  return ({
    type: types.UPDATE_ROUTE_REQUEST,
    routeRequest: routeRequest
  });
};

export const changeRouteParams = () => ({
  type: types.CHANGE_ROUTE,
  show: false,
});

const setRouteParams = params => ({
  type: types.SET_ROUTE_SEARCH_PARAMS,
  params,
});

export const setAppInitialized = () => {
  // required for prerender service
  window.prerenderReady = true;

  return {
    type: types.SET_APP_INITIALIZED,
  };
};

export const searchRouteStarted = () => ({
  type: types.SEARCH_ROUTE_STARTED,
});

export const searchRouteSucceeded = route => ({
  type: types.SEARCH_ROUTE_SUCCEEDED,
  route: route
});

/** @param {SearchRouteError} searchRouteError */
export const searchRouteFailed = (searchRouteError) => ({
  type: types.SEARCH_ROUTE_FAILED,
  searchRouteError: searchRouteError
});

export const toggleShowPassingCitiesInRouteDetails = enabled => {
  return {
    type: types.TOGGLE_SHOW_PASSING_CITIES,
    showPassingCitiesInRouteDetails: enabled,
  };
};

export const checkIfAppIsInitialized = () => dispatch => {
  if (initService.isInited()) {
    dispatch(setAppInitialized());
  }
};

const routeNotFound = () => dispatch => {
  dispatch(routeNotFoundFlag());
};

const routeWithoutRestrictions = (withoutRestrictions) => dispatch => {
  dispatch(routeWithoutRestrictionsFlag(withoutRestrictions));
};

/** @param {RouteRequestPoint} requestPoint */
const createRouteSegmentFromRequestPoint = (requestPoint) =>{ 
  const segment = new RouteSegment({routeDetails: new RouteDetails(), routePoint: new RouteResponsePoint()});

  /** @param {RouteRequestPoint} requestPoint */
  const getNodeTypes = (requestPoint) => {
    let nodeTypes = 0;
    if(requestPoint.isStartPoint)
      nodeTypes = nodeTypes | NODE_TYPES.start;
    if(requestPoint.isEndPoint)
      nodeTypes = nodeTypes | NODE_TYPES.finish;
    if(requestPoint.isIntermediatePoint)
      nodeTypes = nodeTypes | NODE_TYPES.passthrough;
    if(requestPoint.isLoadingPoint)
      nodeTypes = nodeTypes | NODE_TYPES.load;
    if(requestPoint.isUnloadingPoint)
      nodeTypes = nodeTypes | NODE_TYPES.unload;
    return nodeTypes;
  };
  segment.routePoint.geoPoint = requestPoint.geoPoint;
  segment.routePoint.nodeTypes = getNodeTypes(requestPoint);

  segment.routePoint.city = requestPoint.city;
  segment.routePoint.region = requestPoint.region;
  segment.routePoint.country = requestPoint.country;
  segment.routePoint.geoPoint = requestPoint.geoPoint;
  
  return segment;
}
export const calculateRoute = () => async (dispatch, getState) => {

  const state = getState();
  const {
    routeRequest,
    calculations,
    speeds,
    restrictions,
    excludedCities,
    excludedCountries
  } = state;

  if (!routeRequestPointIsSet(routeRequest.from) || !routeRequestPointIsSet(routeRequest.to)) {
    return;
  }

  dispatch(searchRouteStarted());
  const searchRouteParameters = new SearchRouteParameters({
    routeRequest,
    restrictions,
    excludedCities,
    excludedCountries
  });

  const searchRouteResult = await getRouteInfo(searchRouteParameters);
  if (!searchRouteResult) {
    // В случае непредусмотренной ошибки или CanceledError, никак не влияем на отображение текущего маршрута
    return;
  }
  batch(() => {
    if (!searchRouteResult.error) {
      const routeResponse = searchRouteResult.routeResponse;
      if (routeResponse) {
        const {routeInfo, platonRate, status} = routeResponse;

        dispatch(routeWithoutRestrictions(status === ROUTE_FOUND_WITHOUT_RESTRICTIONS));
        dispatch(searchRouteSucceeded(routeResponse));
        dispatch(calculateRouteMetrics(routeInfo, calculations, speeds, platonRate, false));
        dispatch(showResults(true));
        dispatch(savePlatonRate(platonRate));
      }
      else {
        // Маршрут не найден
        /** @type {RouteResponse} */
        const notFoundRoute = {
          routeInfo: {
            segments: []
          },
          visualRoute: {
            routeId: undefined,
            segments: []
          }
        }

        const allRoutePoints = RouteRequest.getAllRoutePoints(routeRequest);
        for (let i = 0; i < allRoutePoints.length; i++) {
          const routeRequestPoint = allRoutePoints[i];
          notFoundRoute.routeInfo.segments.push(createRouteSegmentFromRequestPoint(routeRequestPoint));
        }

        dispatch(routeWithoutRestrictions(false));
        dispatch(searchRouteSucceeded(notFoundRoute));
        dispatch(routeNotFound());
        dispatch(showResults(false));
        dispatch(calculateRouteMetrics(notFoundRoute.routeInfo, calculations, speeds, 0, true));
      }
    } else {
      dispatch(searchRouteFailed(searchRouteResult.error))
    }
  });
};

export const getVisualRouteInBounds = (mapBounds) => async (dispatch, getState) => {
  const state = getState();
  const {
    routeRequest,
    visualRoute: {routeId},
    restrictions,
    excludedCities,
    excludedCountries
  } = state;

  const southWest = mapBounds.getSouthWest();
  const northEast = mapBounds.getNorthEast();
  const searchRouteParameters = new SearchRouteParameters({
    routeRequest,
    restrictions,
    excludedCities,
    excludedCountries,
    mapBounds: {
      southWest: {lat: southWest.lat, lon: southWest.lng},
      northEast: {lat: northEast.lat, lon: northEast.lng}
    },
    routeId
  });

  const visualRouteResult = await getVisualRoute(searchRouteParameters);
  if (!visualRouteResult) {
    // В случае непредусмотренной ошибки или CanceledError, никак не влияем на отображение текущего маршрута
    return;
  }

  if (!visualRouteResult.error) {
    const routeResponse = visualRouteResult.routeResponse;
    if (routeResponse) {
      dispatch(searchRouteSucceeded(
        {
          visualRoute: routeResponse.visualRoute
        }
      ));
    }
  }
  else {
    dispatch(searchRouteFailed(visualRouteResult.error));
  }
}

export const searchRoute = request => dispatch => {
  const {restrictions, calculations} = request;

  batch(() => {
    dispatch(
      setRouteParams({
        restrictions,
        calculations
      }),
    );
    dispatch(calculateRoute());
  });
};

const getCommonStatsSucceeded = stats => ({
  type: types.FETCH_COMMON_STATS_SUCCEEDED,
  stats,
});

const getCommonStatsFailed = stats => ({
  type: types.FETCH_COMMON_STATS_FAILED,
  stats,
});

export const getCommonStats = () => dispatch => {
  api.stats
    .getCommonStats()
    .then(response => dispatch(getCommonStatsSucceeded(response.data)))
    .catch(() => dispatch(getCommonStatsFailed()));
};

/** @param {City[]} cities */
const updateExcludedCities = cities => ({
  type: types.UPDATE_EXCLUDED_CITIES,
  payload: cities,
});

/** @param {Country[]} countries */
const updateExcludedCountries = countries => ({
  type: types.UPDATE_EXCLUDED_COUNTRIES,
  payload: countries,
});

/** @param {City} city */
export const addExcludedCity = city => ({
  type: types.ADD_EXCLUDED_CITY,
  city,
});

/** @param {Country} country */
export const addExcludedCountry = country => ({
  type: types.ADD_EXCLUDED_COUNTRY,
  country,
});

export const removeCityFromExcluded = cityId => ({
  type: types.REMOVE_EXCLUDED_CITY,
  id: cityId
});

export const removeCountryFromExcluded = countryId => ({
  type: types.REMOVE_EXCLUDED_COUNTRY,
  id: countryId
});

export const resetAllExcluded = () => ({
  type: types.RESET_ALL_EXCLUDED,
});

/** @param {City} city */
export const excludeCity = city => (dispatch, getState) => {
  const {id} = city;
  
  const {
    /** @type {RouteRequest} */
    routeRequest
  } = getState();
  const newRouteRequest = routeService.excludeIntermediateCityFromRoute(routeRequest, id);
  batch(() => {
    dispatch(updateRouteRequest(newRouteRequest));
    dispatch(addExcludedCity(city));
    dispatch(calculateRoute());
  });
};

export const excludePassthroughPoint = index => (dispatch, getState) => {
  const {
    /** @type {RouteRequest} */
    routeRequest
  } = getState();
  
  const newRouteRequest = routeService.excludePassthroughPointFromRoute(routeRequest, index);
  batch(() => {
    dispatch(updateRouteRequest(newRouteRequest));
    dispatch(calculateRoute());
  });
};

/** @param {Country} country */
export const excludeCountry = country => dispatch => {
  batch(() => {
    dispatch(addExcludedCountry(country));
    dispatch(calculateRoute());
  });
};

export const returnCityToRoute = cityId => dispatch => {
  batch(() => {
    dispatch(removeCityFromExcluded(cityId));
    dispatch(calculateRoute());
  });
};

export const returnCountryToRoute = countryId => dispatch => {
  batch(() => {
    dispatch(removeCountryFromExcluded(countryId));
    dispatch(calculateRoute());
  });
};

export const resetExcludedAndCalculateRoute = () => dispatch => {
  batch(() => {
    dispatch(resetAllExcluded());
    dispatch(calculateRoute());
  });
};

export const changeRoute = () => dispatch => {
  batch(() => {
    dispatch(changeRouteParams());
    dispatch(resetAllExcluded());
  });
};

export const setRestrictionInitialValue = payload => ({
  type: types.SET_RESTRICTION_INITIAL_VALUE,
  payload,
});

export const setShowLightVersion = showLightVersion => ({
  type: types.SET_SHOW_LIGHT_VERSION,
  showLightVersion,
});

export const toggleSidebar = sidebarToggled => ({
  type: types.TOGGLE_SIDEBAR,
  sidebarToggled,
});

/**
 * @param routeRequest {RouteRequest}
 * @returns {boolean}
 */
const checkWithinCountryShouldBeDisabled = (routeRequest) => {
  const countryIds = [routeRequest.from.getCountryId(), ...routeRequest.through.map(routePoint => routePoint.getCountryId()), routeRequest.to.getCountryId()];
  const unique = countryIds.filter((item, index, all) => all.indexOf(item) === index);
  return unique.length > 1;
};

export const setRouteQueryParams = payload => async (dispatch, getState) => {
  const {name: routePartName, items: parsedPoints} = payload;

  if (typeof parsedPoints === "undefined" || parsedPoints.length === 0) {
    return;
  }

  dispatch(increasePendingRequest());
  const keyPoints = createKeyPointFromParsedPoints(parsedPoints);

  try {

    const {routeRequest: oldRoute} = getState();
    const newRouteRequest = await createRouteRequestFromKeyPoints(keyPoints, oldRoute, routePartName);

    const isWithinCountryShouldBeDisabled = checkWithinCountryShouldBeDisabled(newRouteRequest);

    batch(() => {
      if (isWithinCountryShouldBeDisabled) {
        dispatch(setRestrictionInitialValue({name: "withinCountry", value: false}));
      }
      dispatch(updateRouteRequest(newRouteRequest));
      dispatch(decreasePendingRequest());
    });
  } catch (e) {
    dispatch(decreasePendingRequest());
    console.error(e);
  }
};

export const setExcludedCitiesInitialValue = ids => async dispatch => {
  if (ids.length === 0) return;

  dispatch(increasePendingRequest());
  try{
    /** @type {City[]} */
    const cities = await geoStorages.cities.getByIds(ids);
    batch(() => {
      dispatch(updateExcludedCities(cities));
      dispatch(decreasePendingRequest());
    });
  }
  catch(e){
    console.error(e);
    dispatch(decreasePendingRequest());
  }
};

export const setExcludedCountriesInitialValue = ids => async dispatch => {
  if (ids.length === 0) return;

  dispatch(increasePendingRequest());

  try {
    /** @type {Country[]} */
    const countries = await geoStorages.countries.getByIds(ids);
    batch(() => {
      dispatch(updateExcludedCountries(countries));
      dispatch(decreasePendingRequest());
    });
  } catch (e) {
    console.error(e);
    dispatch(decreasePendingRequest());
  }
};

export const hideErrorNotification = () => ({
  type: types.HIDE_ERROR_NOTIFICATION,
});

export const setUserProfile = ({ profile, isPaidUser }) => ({
  type: types.SET_USER_PROFILE,
  profile,
  isPaidUser,
});

export const initUserProfile = () => dispatch => {
  const atiUser = window.atiUser;
  if (!atiUser) return;

  const isUser = atiUser.is_user ?? atiUser.isUser;
  if (!isUser) return;

  let profile = atiUser.profile || null;
  // Normalize payedServices if snake_case from window.atiUser (shallow copy — don't mutate window.atiUser)
  if (profile && profile.payed_services && !profile.payedServices) {
    profile = { ...profile, payedServices: profile.payed_services };
  }
  const isPaidUser = atiUser.is_paid_user ?? atiUser.isPaidUser ?? false;
  dispatch(setUserProfile({ profile, isPaidUser }));
};

export const init = () => (dispatch) => {
  dispatch(initUserProfile());
  dispatch(calculateRoute());
  dispatch(setAppInitialized());
};

export const calculateRouteMetrics = (routeInfo, calculations, speeds, platonRate, routeNotFound) => {
  const metrics = routeMetricsCalculator(routeInfo, calculations, speeds, platonRate, routeNotFound);
  return {
    type: types.CALCULATE_ROUTE_METRICS,
    metrics
  };
};
